/**
 * WireGraph: the "Wire" mode of the Design studio, an ALTERNATIVE to the Write layout
 * (never a replacement). It draws the issue's pipeline as a real, edge-wired graph so
 * the owner can trace provenance at a glance:
 *
 *   SOURCES (left)  ->  SECTIONS (middle, in issue order)  ->  ISSUE (right, one node)
 *
 * Nodes are laid out in three deterministic columns (each column's nodes evenly
 * distributed top-to-bottom). Behind them, an SVG layer draws a bezier EDGE from every
 * source to each section it feeds (coloured by the source's identity colour) and from
 * every section to the single Issue node (a neutral colour). Selecting a node lifts the
 * selection to the studio (same {@link SelectedNode} model as Write mode, so the shared
 * inspector works identically) and HIGHLIGHTS only that node's edges, dimming the rest
 * so even a dense graph stays legible.
 *
 * The whole graph shares the Write map's visual language: the same source colours, the
 * same {@link treatmentLabel} chips, the same node identity. It is a desktop affordance;
 * the studio only offers Wire mode at >=1024px.
 *
 * The SVG uses a 0..100 x 0..100 percentage view space (preserveAspectRatio="none") so
 * node CSS percent positions and edge coordinates share one coordinate system and scale
 * with the container; strokes stay constant width via vector-effect. No inner scroll:
 * the canvas sizes to its node rows and the page scrolls once.
 */
import type { PipelineSectionNode, PipelineSource, SelectedNode } from './pipeline';
import {
  deriveWireEdges,
  edgeTouchesNode,
  sourceChannelLabel,
  treatmentLabel,
  type WireEdge,
} from './pipeline';

/** A short glyph per section role, echoing the Write map's node icons. Decorative. */
const ROLE_ICON: Record<PipelineSectionNode['role'], string> = {
  intro: '✳',
  story: '★',
  commentary: '✍',
  digest: '≣',
  links: '↗',
  signoff: '~',
};

/**
 * The horizontal centre (in percent) of each column in the 0..100 view space. The
 * source + issue columns are inset ~22% from each edge (not hard against it) so a
 * left-column node - up to ~15rem wide, centred on its column via translate(-50%) -
 * sits fully on-canvas rather than clipping the edge as a thin cramped strip. The
 * three columns are evenly spread (28% gaps) so Sources | Sections | Issue read as
 * three balanced columns. Both the node CSS positions and the SVG edge endpoints
 * derive from these values, so they always line up.
 */
const COLUMN_X = { source: 22, section: 50, issue: 78 } as const;

/** Vertical padding (percent) reserved top and bottom, so nodes never touch the edge. */
const Y_PAD = 10;
const Y_RANGE = 100 - Y_PAD * 2;

/**
 * The vertical centre (percent) of node `index` of `count`, evenly distributed within
 * the padded band. A lone node (count 1, the Issue) sits at the middle.
 */
export function wireNodeY(index: number, count: number): number {
  if (count <= 1) {
    return 50;
  }
  return Y_PAD + ((index + 0.5) / count) * Y_RANGE;
}

/** A resolved node position in the percentage view space. */
interface Point {
  readonly x: number;
  readonly y: number;
}

/** The endpoint key for a node position lookup: its selection kind + id. */
function pointKey(kind: SelectedNode['kind'], id: string): string {
  return `${kind}:${id}`;
}

/** A horizontal S-curve (cubic bezier) between two points in the view space. */
function edgePath(from: Point, to: Point): string {
  const midX = (from.x + to.x) / 2;
  return `M ${String(from.x)} ${String(from.y)} C ${String(midX)} ${String(from.y)}, ${String(
    midX,
  )} ${String(to.y)}, ${String(to.x)} ${String(to.y)}`;
}

function isSelected(selected: SelectedNode | null, kind: SelectedNode['kind'], id: string): boolean {
  return selected !== null && selected.kind === kind && selected.id === id;
}

export function WireGraph({
  sources,
  sectionNodes,
  issueStatus,
  selected,
  highlightId = null,
  busy = false,
  onSelect,
}: {
  readonly sources: readonly PipelineSource[];
  readonly sectionNodes: readonly PipelineSectionNode[];
  /** The issue's lifecycle word for the Issue node readout (draft / ready / …). */
  readonly issueStatus: string | null;
  readonly selected: SelectedNode | null;
  /** The block id currently cross-highlighted (a section node lights when its block is). */
  readonly highlightId?: string | null;
  /** True while the issue is loading/generating, so the empty state reads as "drafting". */
  readonly busy?: boolean;
  readonly onSelect: (node: SelectedNode) => void;
}): React.JSX.Element {
  // No issue yet (or still drafting the first one): a calm empty state, never a broken
  // canvas. Once sections exist the graph draws even mid-regenerate (the last nodes stay).
  if (sectionNodes.length === 0) {
    return (
      <section className="nlv__panel nlv__wire nlv__wire--empty" aria-labelledby="nlv-wire-heading">
        <div className="nlv__wire-head">
          <h2 id="nlv-wire-heading" className="nlv__panel-title">
            Pipeline
          </h2>
        </div>
        <p className="nlv__wire-empty-note" role={busy ? 'status' : undefined}>
          {busy
            ? 'Drafting your issue, the pipeline appears here in a moment…'
            : 'As you keep sources on the left, your issue pipeline is drawn here.'}
        </p>
      </section>
    );
  }

  // Resolve every node's position once, keyed by its selection identity, so the edges
  // can look up their endpoints. Deterministic: fixed column x, evenly spaced y.
  const positions = new Map<string, Point>();
  sources.forEach((source, index) => {
    positions.set(pointKey('source', source.key), {
      x: COLUMN_X.source,
      y: wireNodeY(index, sources.length),
    });
  });
  sectionNodes.forEach((node, index) => {
    positions.set(pointKey('section', node.blockId), {
      x: COLUMN_X.section,
      y: wireNodeY(index, sectionNodes.length),
    });
  });
  positions.set(pointKey('issue', 'issue'), { x: COLUMN_X.issue, y: 50 });

  const edges: readonly WireEdge[] = deriveWireEdges(sources, sectionNodes);
  const keptCount = sectionNodes.filter((node) => node.status !== 'dismissed').length;
  // The canvas grows with the taller column so nodes never overlap; the page scrolls once.
  const rows = Math.max(sources.length, sectionNodes.length, 1);

  return (
    <section className="nlv__panel nlv__wire" aria-labelledby="nlv-wire-heading">
      <div className="nlv__wire-head">
        <h2 id="nlv-wire-heading" className="nlv__panel-title">
          Pipeline
        </h2>
        <p className="nlv__wire-hint">
          Sources feed sections; every section flows into your issue. Select a node to
          trace just its wires.
        </p>
      </div>

      {sources.length > 0 && (
        <ul className="nlv__map-legend" aria-label="Source colours">
          {sources.map((source) => (
            <li key={source.key} className="nlv__map-legend-item" title={source.name}>
              <span
                className="nlv__map-dot"
                style={{ background: source.color }}
                aria-hidden="true"
              />
              {source.shortName}
            </li>
          ))}
        </ul>
      )}

      <div
        className="nlv__wire-canvas"
        role="group"
        aria-label="Issue pipeline graph"
        style={{ '--wire-rows': rows } as React.CSSProperties}
      >
        {/* EDGES: an SVG layer BEHIND the nodes. Percentage view space so node CSS
            percents and edge coordinates align; non-scaling strokes keep width. */}
        <svg
          className="nlv__wire-edges"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {edges.map((edge) => {
            const from = positions.get(pointKey(edge.from.kind, edge.from.id));
            const to = positions.get(pointKey(edge.to.kind, edge.to.id));
            if (from === undefined || to === undefined) {
              return null;
            }
            const incident = edgeTouchesNode(edge, selected);
            const dim = selected !== null && !incident;
            return (
              <path
                key={edge.id}
                data-edge-id={edge.id}
                className={`nlv__wire-edge nlv__wire-edge--${edge.kind}${
                  selected !== null && incident ? ' is-incident' : ''
                }${dim ? ' is-dim' : ''}`}
                d={edgePath(from, to)}
                fill="none"
                stroke={edge.color}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </svg>

        {/* SOURCES column */}
        {sources.map((source, index) => (
          <button
            key={source.key}
            type="button"
            className={`nlv__wire-node nlv__wire-node--source${
              isSelected(selected, 'source', source.key) ? ' is-selected' : ''
            }`}
            style={
              {
                '--src-color': source.color,
                left: `${String(COLUMN_X.source)}%`,
                top: `${String(wireNodeY(index, sources.length))}%`,
              } as React.CSSProperties
            }
            aria-pressed={isSelected(selected, 'source', source.key)}
            onClick={() => onSelect({ kind: 'source', id: source.key })}
          >
            <span className="nlv__wire-node-name" title={source.name}>
              {source.shortName}
            </span>
            <span className="nlv__wire-node-sub">{sourceChannelLabel(source.ref)}</span>
          </button>
        ))}

        {/* SECTIONS column, in issue reading order */}
        {sectionNodes.map((node, index) => (
          <button
            key={node.blockId}
            type="button"
            className={`nlv__wire-node nlv__wire-node--section${
              node.status === 'dismissed' ? ' is-dismissed' : ''
            }${isSelected(selected, 'section', node.blockId) ? ' is-selected' : ''}${
              highlightId === node.blockId ? ' is-active' : ''
            }`}
            style={{
              left: `${String(COLUMN_X.section)}%`,
              top: `${String(wireNodeY(index, sectionNodes.length))}%`,
            }}
            aria-pressed={isSelected(selected, 'section', node.blockId)}
            onClick={() => onSelect({ kind: 'section', id: node.blockId })}
          >
            <span className="nlv__wire-node-ico" aria-hidden="true">
              {ROLE_ICON[node.role]}
            </span>
            <span className="nlv__wire-node-body">
              <span className="nlv__wire-node-name" title={node.title}>
                {node.title}
              </span>
              {node.treatment !== null && (
                <span className="nlv__treatment nlv__treatment--static nlv__treatment--ai">
                  {treatmentLabel(node.treatment)}
                </span>
              )}
            </span>
          </button>
        ))}

        {/* ISSUE node (single, right column) */}
        <button
          type="button"
          className={`nlv__wire-node nlv__wire-node--issue${
            isSelected(selected, 'issue', 'issue') ? ' is-selected' : ''
          }`}
          style={{ left: `${String(COLUMN_X.issue)}%`, top: '50%' }}
          aria-pressed={isSelected(selected, 'issue', 'issue')}
          onClick={() => onSelect({ kind: 'issue', id: 'issue' })}
        >
          <span className="nlv__wire-node-ico nlv__wire-node-ico--issue" aria-hidden="true">
            ✉
          </span>
          <span className="nlv__wire-node-body">
            <span className="nlv__wire-node-name">Issue</span>
            <span className="nlv__wire-node-sub">
              {`${String(keptCount)} section${keptCount === 1 ? '' : 's'} · ${
                issueStatus ?? 'draft'
              }`}
            </span>
          </span>
        </button>

        {/* SEAM (deferred): drag-to-rebind (E3) lands here — a source node dragged onto a
            section would add/replace a binding. Wire v1 is visualize + select only, so no
            pointer-drag handlers are wired yet; the edge model above is the surface it
            will mutate. */}
      </div>
    </section>
  );
}
